import { getFCMToken } from '../firebase/config';
import { apiPost } from '../api/apiREST';
import { toast } from 'react-toastify';

/**
 * Register FCM token for the current user
 * This function should be called after successful login or token refresh
 * @param showSuccessToast - Whether to show success toast (only true when user manually grants permission)
 */
export const registerFCMTokenForUser = async (showSuccessToast: boolean = false): Promise<void> => {
  try {
    console.log('🔄 Starting FCM token registration...');
    
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('⚠️ Notifications not supported in this browser');
      return;
    }

    // Check if we have permission
    if (Notification.permission !== 'granted') {
      console.warn('⚠️ Notification permission not granted (status:', Notification.permission, ')');
      return;
    }

    console.log('✅ Permission check passed, getting FCM token...');

    // Add timeout to prevent infinite waiting
    const tokenPromise = getFCMToken();
    const timeoutPromise = new Promise<null>((_, reject) => {
      setTimeout(() => reject(new Error('FCM token request timeout')), 10000); // 10 second timeout
    });

    const token = await Promise.race([tokenPromise, timeoutPromise]);
    
    if (!token) {
      console.warn('⚠️ Failed to get FCM token');
      return;
    }

    // Log FCM token to console
    console.log('🔔 FCM Token for registration:', token);
    console.log('📱 Token length:', token.length);
    console.log('⏰ Registration time:', new Date().toISOString());

    console.log('📤 Sending token to backend...');

    // Register token with backend with timeout
    const apiPromise = apiPost('notifications/register-token/', {
      token: token
    });
    
    const apiTimeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('API request timeout')), 15000); // 15 second timeout
    });

    const response = await Promise.race([apiPromise, apiTimeoutPromise]);

    if (response.error) {
      console.error('❌ Failed to register FCM token:', response.error);
      throw new Error(response.error);
    }

    console.log('✅ FCM token registered successfully');
    console.log('📊 Response:', response.data);
    
    // Show success toast only when explicitly requested (manual permission grant)
    if (showSuccessToast) {
      toast.success('اعلانات فعال شد');
    }
    
  } catch (error: any) {
    console.error('❌ Error registering FCM token:', error);
    // Don't show error toast to avoid spam, just log it
  }
};

/**
 * Initialize FCM notifications for the user
 * This includes requesting permission and registering token
 */
export const initializeFCMForUser = async (): Promise<void> => {
  try {
    console.log('🚀 Starting FCM initialization...');
    
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('⚠️ Notifications not supported in this browser');
      return;
    }

    console.log('✅ Notifications supported, checking permission...');

    // Request permission if not already granted
    let permissionGrantedManually = false;
    if (Notification.permission === 'default') {
      console.log('🔔 Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('🔔 Permission result:', permission);
      
      if (permission !== 'granted') {
        console.warn('⚠️ Notification permission denied');
        return;
      }
      permissionGrantedManually = true; // User just granted permission
    } else if (Notification.permission !== 'granted') {
      console.warn('⚠️ Notification permission not granted (current status:', Notification.permission, ')');
      return;
    }

    console.log('✅ Permission granted, registering token...');
    
    // Register the token - only show toast if permission was just granted
    await registerFCMTokenForUser(permissionGrantedManually);
    
    console.log('✅ FCM initialization completed successfully');
    
  } catch (error) {
    console.error('❌ Error initializing FCM:', error);
    // Don't throw the error to prevent blocking the main flow
  }
};
